import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS } from "./adminStyles";

const API_URL = getBaseURL();

const ManageWithdrawalsInner = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!token) navigate("/winrox11-super-admin");
    else fetchPendingWithdrawals();
  }, [token, navigate]);

  const fetchPendingWithdrawals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/withdrawals/pending`, { headers: { Authorization: `Bearer ${token}` } });
      setWithdrawals(res.data);
    } catch (err) {
      toast.error("Failed to load pending withdrawals.");
    } finally { setLoading(false); }
  };

  const handleApprove = async (withdrawId, amount, name) => {
    if (!window.confirm(`Approve ₹${amount} for ${name}?\n\nMake sure you have transferred the money to the bank account shown.`)) return;
    try {
      await axios.post(`${API_URL}/admin/withdrawals/approve/${withdrawId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`₹${amount} withdrawal approved for ${name}!`);
      fetchPendingWithdrawals();
    } catch (err) {
      toast.error("Approval failed: " + (err.response?.data?.error || err.message));
    }
  };

  const handleReject = async (withdrawId, amount, name) => {
    if (!window.confirm(`Reject ₹${amount} for ${name}? The amount will be refunded to user's wallet.`)) return;
    try {
      await axios.post(`${API_URL}/admin/withdrawals/reject/${withdrawId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.warn(`₹${amount} withdrawal rejected and refunded to ${name}.`);
      fetchPendingWithdrawals();
    } catch (err) {
      toast.error("Rejection failed: " + (err.response?.data?.error || err.message));
    }
  };

  const totalPending = withdrawals.reduce((s, w) => s + Number(w.amount || 0), 0);

  return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <button onClick={() => navigate("/winrox11-super-admin/dashboard")} style={S.backBtn}>← Back to Dashboard</button>

      <div style={S.topBar}>
        <div>
          <div style={S.pageTitle}>💸 Pending Withdrawals</div>
          <div style={S.pageSubtitle}>User Payout Requests</div>
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.red}30`, borderRadius: "12px", padding: "12px 18px", textAlign: "center" }}>
            <div style={{ fontSize: "22px", fontWeight: 800, color: COLORS.red }}>{withdrawals.length}</div>
            <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>Pending</div>
          </div>
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.green}30`, borderRadius: "12px", padding: "12px 18px", textAlign: "center" }}>
            <div style={{ fontSize: "22px", fontWeight: 800, color: COLORS.green }}>₹{totalPending.toLocaleString("en-IN")}</div>
            <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>Total Payout</div>
          </div>
        </div>
      </div>

      {/* Warning tip */}
      <div style={{ background: COLORS.indigo + "10", border: `1px solid ${COLORS.indigo}30`, borderRadius: "12px", padding: "14px 18px", marginBottom: "24px", display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <span style={{ fontSize: "18px" }}>🏦</span>
        <div>
          <div style={{ fontSize: "12px", fontWeight: 700, color: COLORS.indigoLight, marginBottom: "4px" }}>Payout Instructions</div>
          <div style={{ fontSize: "12px", color: COLORS.textMuted }}>Copy the account details below, transfer the amount via your banking app, and then click **Approve**. If you click **Reject**, the money is automatically returned to the user's game wallet.</div>
        </div>
      </div>

      {loading ? (
        <div style={S.loading}>Loading pending withdrawals...</div>
      ) : withdrawals.length === 0 ? (
        <div style={{ ...S.empty, fontSize: "16px" }}>
          <div style={{ fontSize: "48px", marginBottom: "12px" }}>✅</div>
          All withdrawal requests have been processed!
        </div>
      ) : (
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "16px", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
              <thead>
                <tr style={{ background: COLORS.inputBg }}>
                  {["User Details", "Bank Account Info", "Amount", "Date", "Actions"].map(h => (
                    <th key={h} style={{
                      padding: "14px 16px",
                      fontSize: "10px",
                      fontWeight: 700,
                      color: COLORS.textMuted,
                      letterSpacing: "1.5px",
                      textTransform: "uppercase",
                      textAlign: h === "Actions" ? "center" : "left",
                      borderBottom: `1px solid ${COLORS.cardBorder}`,
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((w, idx) => (
                  <tr key={w.withdraw_id} className="admin-row-hover" style={{ borderBottom: `1px solid ${COLORS.cardBorder}`, background: idx % 2 === 0 ? COLORS.card : COLORS.surface, transition: "background 0.1s" }}>
                    <td style={{ padding: "16px" }}>
                      <div style={{ fontWeight: 700, fontSize: "14px", color: COLORS.text }}>{w.first_name} {w.last_name}</div>
                      <div style={{ fontSize: "11px", color: COLORS.textMuted, marginTop: "3px" }}>ID: {w.user_id} · {w.mobile}</div>
                    </td>
                    <td style={{ padding: "16px" }}>
                      {w.account_number ? (
                        <>
                          <div style={{ fontSize: "13px", fontWeight: 700, color: COLORS.indigoLight }}>{w.account_number}</div>
                          <div style={{ fontSize: "11px", color: COLORS.textMid, marginTop: "2px" }}>
                            {w.bank_name} · IFSC: <span style={{ color: COLORS.text }}>{w.ifsc}</span>
                          </div>
                          <div style={{ fontSize: "10px", color: COLORS.textMuted }}>Name: {w.holder_name}</div>
                        </>
                      ) : (
                        <div style={{ color: COLORS.red, fontSize: "11px", fontWeight: 700 }}>⚠️ No bank details found</div>
                      )}
                    </td>
                    <td style={{ padding: "16px" }}>
                      <div style={{ fontSize: "20px", fontWeight: 800, color: COLORS.red }}>₹{Number(w.amount).toLocaleString("en-IN")}</div>
                    </td>
                    <td style={{ padding: "16px", fontSize: "12px", color: COLORS.textMuted }}>
                      {new Date(w.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td style={{ padding: "16px" }}>
                      <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <button
                          className="admin-btn-hover"
                          disabled={!w.account_number}
                          onClick={() => handleApprove(w.withdraw_id, w.amount, w.first_name)}
                          style={{ padding: "8px 16px", background: COLORS.green + "20", color: COLORS.green, border: `1px solid ${COLORS.green}40`, borderRadius: "8px", cursor: w.account_number ? "pointer" : "not-allowed", fontSize: "12px", fontWeight: 700, transition: "all 0.15s", opacity: w.account_number ? 1 : 0.5 }}
                        >
                          ✅ Paid
                        </button>
                        <button
                          className="admin-btn-hover"
                          onClick={() => handleReject(w.withdraw_id, w.amount, w.first_name)}
                          style={{ padding: "8px 16px", background: COLORS.red + "15", color: COLORS.red, border: `1px solid ${COLORS.red}30`, borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: 700, transition: "all 0.15s" }}
                        >
                          ❌ Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const ManageWithdrawals = () => (
  <ToastProvider>
    <ManageWithdrawalsInner />
  </ToastProvider>
);

export default ManageWithdrawals;
